import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { courseApi } from '../../services/api';
import { PlusIcon, TrashIcon } from '../../components/icons';

interface CourseModule {
  title: string;
  description: string;
  duration: number;
  topics: string[];
  learningObjectives: string[];
  assignments?: string[];
  resources?: string[];
}

interface CourseDetail {
  _id?: string;
  courseId: string;
  overview: string;
  prerequisites: string[];
  learningOutcomes: string[];
  modules: CourseModule[];
  totalDuration: number;
  skillLevel: 'Beginner' | 'Intermediate' | 'Advanced';
  technologies: string[];
  certification: boolean;
}

const ManageCourseDetailsPage: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const [courseDetail, setCourseDetail] = useState<CourseDetail>({
    courseId: courseId || '',
    overview: '',
    prerequisites: [''],
    learningOutcomes: [''],
    modules: [{
      title: '',
      description: '',
      duration: 0,
      topics: [''],
      learningObjectives: [''],
      assignments: [''],
      resources: ['']
    }],
    totalDuration: 0,
    skillLevel: 'Beginner',
    technologies: [''],
    certification: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchCourseDetail = async () => {
      try {
        const response = await courseApi.getCourseDetail(courseId!);
        if (response.data.data.courseDetail) {
          setCourseDetail(response.data.data.courseDetail);
        }
      } catch (error) {
        console.error('Error fetching course details:', error);
      } finally {
        setLoading(false);
      }
    };

    if (courseId) {
      fetchCourseDetail();
    }
  }, [courseId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (courseDetail._id) {
        await courseApi.updateCourseDetail(courseId!, courseDetail);
      } else {
        await courseApi.createCourseDetail(courseId!, courseDetail);
      }
      navigate('/admin/courses');
    } catch (error) {
      console.error('Error saving course details:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleArrayFieldChange = (
    field: keyof Pick<CourseDetail, 'prerequisites' | 'learningOutcomes' | 'technologies'>,
    index: number,
    value: string
  ) => {
    setCourseDetail(prev => ({
      ...prev,
      [field]: (prev[field] as string[]).map((item, i) => (i === index ? value : item))
    }));
  };

  const addArrayField = (
    field: keyof Pick<CourseDetail, 'prerequisites' | 'learningOutcomes' | 'technologies'>
  ) => {
    setCourseDetail(prev => ({
      ...prev,
      [field]: [...(prev[field] as string[]), '']
    }));
  };

  const removeArrayField = (
    field: keyof Pick<CourseDetail, 'prerequisites' | 'learningOutcomes' | 'technologies'>,
    index: number
  ) => {
    setCourseDetail(prev => ({
      ...prev,
      [field]: (prev[field] as string[]).filter((_, i) => i !== index)
    }));
  };

  const handleModuleChange = (index: number, field: keyof CourseModule, value: any) => {
    setCourseDetail(prev => ({
      ...prev,
      modules: prev.modules.map((module, i) => 
        i === index ? { ...module, [field]: value } : module
      )
    }));
  };

  const handleModuleArrayChange = (
    moduleIndex: number,
    field: keyof CourseModule,
    itemIndex: number,
    value: string
  ) => {
    setCourseDetail(prev => ({
      ...prev,
      modules: prev.modules.map((module, i) => 
        i === moduleIndex ? {
          ...module,
          [field]: (module[field] as string[]).map((item, j) => 
            j === itemIndex ? value : item
          )
        } : module
      )
    }));
  };

  const addModule = () => {
    setCourseDetail(prev => ({
      ...prev,
      modules: [...prev.modules, {
        title: '',
        description: '',
        duration: 0,
        topics: [''],
        learningObjectives: [''],
        assignments: [''],
        resources: ['']
      }]
    }));
  };

  const removeModule = (index: number) => {
    setCourseDetail(prev => ({
      ...prev,
      modules: prev.modules.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/courses')}
            className="flex items-center text-neutral-400 hover:text-white transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 mr-2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
            </svg>
            Back to Courses
          </button>
          <h1 className="text-2xl font-semibold text-white">Manage Course Details</h1>
        </div>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-neutral-dark rounded-xl p-6">
          <h2 className="text-xl font-medium text-white mb-4">Basic Information</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-neutral-light mb-1">
                Overview
              </label>
              <textarea
                value={courseDetail.overview}
                onChange={(e) => setCourseDetail(prev => ({ ...prev, overview: e.target.value }))}
                className="w-full bg-neutral-darker text-white rounded-lg p-3 focus:ring-2 focus:ring-primary"
                rows={4}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-light mb-1">
                Skill Level
              </label>
              <select
                value={courseDetail.skillLevel}
                onChange={(e) => setCourseDetail(prev => ({ ...prev, skillLevel: e.target.value as 'Beginner' | 'Intermediate' | 'Advanced' }))}
                className="w-full bg-neutral-darker text-white rounded-lg p-3 focus:ring-2 focus:ring-primary"
              >
                <option value="Beginner">Beginner</option>
                <option value="Intermediate">Intermediate</option>
                <option value="Advanced">Advanced</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-light mb-1">
                Prerequisites
              </label>
              {courseDetail.prerequisites.map((prereq, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <input
                    value={prereq}
                    onChange={(e) => handleArrayFieldChange('prerequisites', index, e.target.value)}
                    className="flex-1 bg-neutral-darker text-white rounded-lg p-3 focus:ring-2 focus:ring-primary"
                    placeholder="Enter prerequisite"
                  />
                  <button
                    type="button"
                    onClick={() => removeArrayField('prerequisites', index)}
                    className="text-red-500 hover:text-red-400 p-2"
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => addArrayField('prerequisites')}
                className="text-primary hover:text-primary-light flex items-center gap-1"
              >
                <PlusIcon className="w-4 h-4" />
                Add Prerequisite
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-light mb-1">
                Learning Outcomes
              </label>
              {courseDetail.learningOutcomes.map((outcome, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <input
                    value={outcome}
                    onChange={(e) => handleArrayFieldChange('learningOutcomes', index, e.target.value)}
                    className="flex-1 bg-neutral-darker text-white rounded-lg p-3 focus:ring-2 focus:ring-primary"
                    placeholder="Enter learning outcome"
                  />
                  <button
                    type="button"
                    onClick={() => removeArrayField('learningOutcomes', index)}
                    className="text-red-500 hover:text-red-400 p-2"
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => addArrayField('learningOutcomes')}
                className="text-primary hover:text-primary-light flex items-center gap-1"
              >
                <PlusIcon className="w-4 h-4" />
                Add Learning Outcome
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-light mb-1">
                Technologies
              </label>
              {courseDetail.technologies.map((tech, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <input
                    value={tech}
                    onChange={(e) => handleArrayFieldChange('technologies', index, e.target.value)}
                    className="flex-1 bg-neutral-darker text-white rounded-lg p-3 focus:ring-2 focus:ring-primary"
                    placeholder="Enter technology"
                  />
                  <button
                    type="button"
                    onClick={() => removeArrayField('technologies', index)}
                    className="text-red-500 hover:text-red-400 p-2"
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => addArrayField('technologies')}
                className="text-primary hover:text-primary-light flex items-center gap-1"
              >
                <PlusIcon className="w-4 h-4" />
                Add Technology
              </button>
            </div>
          </div>
        </div>

        <div className="bg-neutral-dark rounded-xl p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-medium text-white">Course Modules</h2>
            <button
              type="button"
              onClick={addModule}
              className="bg-primary hover:bg-primary-dark text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
            >
              <PlusIcon className="w-5 h-5" />
              Add Module
            </button>
          </div>

          <div className="space-y-6">
            {courseDetail.modules.map((module, moduleIndex) => (
              <div key={moduleIndex} className="border border-neutral-darker rounded-lg p-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-white">Module {moduleIndex + 1}</h3>
                  <button
                    type="button"
                    onClick={() => removeModule(moduleIndex)}
                    className="text-red-500 hover:text-red-400 p-2"
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-light mb-1">
                      Title
                    </label>
                    <input
                      value={module.title}
                      onChange={(e) => handleModuleChange(moduleIndex, 'title', e.target.value)}
                      className="w-full bg-neutral-darker text-white rounded-lg p-3 focus:ring-2 focus:ring-primary"
                      placeholder="Enter module title"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-light mb-1">
                      Description
                    </label>
                    <textarea
                      value={module.description}
                      onChange={(e) => handleModuleChange(moduleIndex, 'description', e.target.value)}
                      className="w-full bg-neutral-darker text-white rounded-lg p-3 focus:ring-2 focus:ring-primary"
                      rows={3}
                      placeholder="Enter module description"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-light mb-1">
                      Duration (hours)
                    </label>
                    <input
                      type="number"
                      value={module.duration}
                      onChange={(e) => handleModuleChange(moduleIndex, 'duration', Number(e.target.value))}
                      className="w-full bg-neutral-darker text-white rounded-lg p-3 focus:ring-2 focus:ring-primary"
                      min="0"
                      step="0.5"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-light mb-1">
                      Topics
                    </label>
                    {module.topics.map((topic, topicIndex) => (
                      <div key={topicIndex} className="flex gap-2 mb-2">
                        <input
                          value={topic}
                          onChange={(e) => handleModuleArrayChange(moduleIndex, 'topics', topicIndex, e.target.value)}
                          className="flex-1 bg-neutral-darker text-white rounded-lg p-3 focus:ring-2 focus:ring-primary"
                          placeholder="Enter topic"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const newModules = [...courseDetail.modules];
                            newModules[moduleIndex].topics = module.topics.filter((_, i) => i !== topicIndex);
                            setCourseDetail(prev => ({ ...prev, modules: newModules }));
                          }}
                          className="text-red-500 hover:text-red-400 p-2"
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => {
                        const newModules = [...courseDetail.modules];
                        newModules[moduleIndex].topics.push('');
                        setCourseDetail(prev => ({ ...prev, modules: newModules }));
                      }}
                      className="text-primary hover:text-primary-light flex items-center gap-1"
                    >
                      <PlusIcon className="w-4 h-4" />
                      Add Topic
                    </button>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-light mb-1">
                      Learning Objectives
                    </label>
                    {module.learningObjectives.map((objective, objectiveIndex) => (
                      <div key={objectiveIndex} className="flex gap-2 mb-2">
                        <input
                          value={objective}
                          onChange={(e) => handleModuleArrayChange(moduleIndex, 'learningObjectives', objectiveIndex, e.target.value)}
                          className="flex-1 bg-neutral-darker text-white rounded-lg p-3 focus:ring-2 focus:ring-primary"
                          placeholder="Enter learning objective"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const newModules = [...courseDetail.modules];
                            newModules[moduleIndex].learningObjectives = module.learningObjectives.filter((_, i) => i !== objectiveIndex);
                            setCourseDetail(prev => ({ ...prev, modules: newModules }));
                          }}
                          className="text-red-500 hover:text-red-400 p-2"
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => {
                        const newModules = [...courseDetail.modules];
                        newModules[moduleIndex].learningObjectives.push('');
                        setCourseDetail(prev => ({ ...prev, modules: newModules }));
                      }}
                      className="text-primary hover:text-primary-light flex items-center gap-1"
                    >
                      <PlusIcon className="w-4 h-4" />
                      Add Learning Objective
                    </button>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-light mb-1">
                      Assignments
                    </label>
                    {module.assignments?.map((assignment, assignmentIndex) => (
                      <div key={assignmentIndex} className="flex gap-2 mb-2">
                        <input
                          value={assignment}
                          onChange={(e) => handleModuleArrayChange(moduleIndex, 'assignments', assignmentIndex, e.target.value)}
                          className="flex-1 bg-neutral-darker text-white rounded-lg p-3 focus:ring-2 focus:ring-primary"
                          placeholder="Enter assignment"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const newModules = [...courseDetail.modules];
                            newModules[moduleIndex].assignments = module.assignments?.filter((_, i) => i !== assignmentIndex);
                            setCourseDetail(prev => ({ ...prev, modules: newModules }));
                          }}
                          className="text-red-500 hover:text-red-400 p-2"
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => {
                        const newModules = [...courseDetail.modules];
                        if (!newModules[moduleIndex].assignments) {
                          newModules[moduleIndex].assignments = [];
                        }
                        newModules[moduleIndex].assignments?.push('');
                        setCourseDetail(prev => ({ ...prev, modules: newModules }));
                      }}
                      className="text-primary hover:text-primary-light flex items-center gap-1"
                    >
                      <PlusIcon className="w-4 h-4" />
                      Add Assignment
                    </button>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-light mb-1">
                      Resources
                    </label>
                    {module.resources?.map((resource, resourceIndex) => (
                      <div key={resourceIndex} className="flex gap-2 mb-2">
                        <input
                          value={resource}
                          onChange={(e) => handleModuleArrayChange(moduleIndex, 'resources', resourceIndex, e.target.value)}
                          className="flex-1 bg-neutral-darker text-white rounded-lg p-3 focus:ring-2 focus:ring-primary"
                          placeholder="Enter resource"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const newModules = [...courseDetail.modules];
                            newModules[moduleIndex].resources = module.resources?.filter((_, i) => i !== resourceIndex);
                            setCourseDetail(prev => ({ ...prev, modules: newModules }));
                          }}
                          className="text-red-500 hover:text-red-400 p-2"
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => {
                        const newModules = [...courseDetail.modules];
                        if (!newModules[moduleIndex].resources) {
                          newModules[moduleIndex].resources = [];
                        }
                        newModules[moduleIndex].resources?.push('');
                        setCourseDetail(prev => ({ ...prev, modules: newModules }));
                      }}
                      className="text-primary hover:text-primary-light flex items-center gap-1"
                    >
                      <PlusIcon className="w-4 h-4" />
                      Add Resource
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-4">
          <button
            type="button"
            onClick={() => navigate('/admin/courses')}
            className="px-6 py-2 text-neutral-light hover:text-white transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="bg-primary hover:bg-primary-dark text-white px-6 py-2 rounded-lg transition-colors disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Course Details'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ManageCourseDetailsPage; 